/**
 * Settings — "Configurability without forks" (ARCHITECTURE.md).
 *
 * Every company thinks its workflow is unique. It mostly isn't, but the parts that are live in
 * data: job types, custom field definitions, checklists per job type, price lists, materials.
 * This screen is where a company's "we're different" is absorbed as rows instead of becoming a
 * branch nobody can merge.
 *
 * The one place the product says no, and says it out loud here: the job state machine. Sync
 * correctness depends on transitions being total, finite, and known to both the device and the
 * server (shared/job-state.ts). A company may want an extra status; it does not get a node in
 * that graph. The tab below states that in Arabic rather than hiding it, because the answer
 * lands better before the sale than after.
 */

import { useState } from "react";
import { JOB_STATES, JOB_STATE_AR, allowedTransitions } from "@fanni/shared/job-state";
import { ROLE_AR } from "../lib/auth.js";
import { fmtDateTime } from "../lib/format.js";
import {
  useChecklists,
  useCreateChecklist,
  useCreateCustomField,
  useCreateJobType,
  useCreateUser,
  useCustomFields,
  useDevices,
  useJobTypes,
  useUpdateUser,
  useUsers,
} from "../lib/queries.js";
import type { Role } from "../lib/types.js";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
  Note,
  PageHeader,
  Select,
  Table,
  Td,
  Th,
  Tr,
  cx,
} from "../ui/kit.js";

const TABS = [
  { key: "users", label: "المستخدمون والفنيون" },
  { key: "job-types", label: "أنواع المهام" },
  { key: "checklists", label: "قوائم الفحص" },
  { key: "fields", label: "الحقول المخصّصة" },
  { key: "company", label: "بيانات الشركة" },
  { key: "states", label: "حالات المهمة" },
] as const;

type Tab = (typeof TABS)[number]["key"];

export function Settings() {
  const [tab, setTab] = useState<Tab>("users");

  return (
    <>
      <PageHeader
        title="الإعدادات"
        subtitle="ما يختلف بين شركة وأخرى يُضبَط هنا كبيانات — لا كنسخة معدّلة من البرنامج."
      />

      <div className="mb-4 flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cx(
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition",
              tab === t.key
                ? "border-accent text-accent"
                : "border-transparent text-ink-soft hover:text-ink",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "users" ? <Users /> : null}
      {tab === "job-types" ? <JobTypes /> : null}
      {tab === "checklists" ? <Checklists /> : null}
      {tab === "fields" ? <CustomFields /> : null}
      {tab === "company" ? <Company /> : null}
      {tab === "states" ? <States /> : null}
    </>
  );
}

// ── users ────────────────────────────────────────────────────────────────────

function Users() {
  const { data, isLoading, error } = useUsers();
  const update = useUpdateUser();
  const [adding, setAdding] = useState(false);
  const [devicesFor, setDevicesFor] = useState<{ id: string; name: string } | null>(null);

  return (
    <>
      {error ? <ErrorNote error={error} className="mb-4" /> : null}
      {update.error ? <ErrorNote error={update.error} className="mb-4" /> : null}

      <Card
        title="المستخدمون"
        actions={
          <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
            مستخدم جديد
          </Button>
        }
        bodyClassName=""
      >
        {isLoading ? (
          <Loading />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>الاسم</Th>
                <Th>الدور</Th>
                <Th>الهاتف</Th>
                <Th>البريد</Th>
                <Th>الحالة</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {(data?.users ?? []).map((u) => (
                <Tr key={u.id}>
                  <Td className="font-medium">{u.name}</Td>
                  <Td>
                    <Badge tone={u.role === "technician" ? "info" : "neutral"}>{ROLE_AR[u.role]}</Badge>
                  </Td>
                  <Td>
                    <span className="num">{u.phone}</span>
                  </Td>
                  <Td>
                    <span className="num text-ink-soft">{u.email ?? "—"}</span>
                  </Td>
                  <Td>
                    <Badge tone={u.active ? "good" : "neutral"}>{u.active ? "فعّال" : "معطّل"}</Badge>
                  </Td>
                  <Td>
                    <span className="flex gap-1">
                      {u.role === "technician" ? (
                        <Button size="sm" variant="ghost" onClick={() => setDevicesFor({ id: u.id, name: u.name })}>
                          أجهزته
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={update.isPending}
                        onClick={() => update.mutate({ id: u.id, active: !u.active })}
                      >
                        {u.active ? "تعطيل" : "تفعيل"}
                      </Button>
                    </span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {adding ? <NewUser onClose={() => setAdding(false)} /> : null}
      {devicesFor ? <DevicesModal user={devicesFor} onClose={() => setDevicesFor(null)} /> : null}
    </>
  );
}

function NewUser({ onClose }: { onClose: () => void }) {
  const create = useCreateUser();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    role: "technician" as Role,
    pin: "",
    password: "",
  });

  const isTech = form.role === "technician";
  const valid =
    form.name.trim().length > 0 &&
    form.phone.trim().length >= 6 &&
    (isTech ? /^\d{4,8}$/.test(form.pin) : form.password.length >= 8);

  return (
    <Modal title="مستخدم جديد" onClose={onClose}>
      {create.error ? <ErrorNote error={create.error} className="mb-3" /> : null}

      <div className="space-y-3">
        <Field label="الاسم">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="الدور">
          <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
            {(Object.keys(ROLE_AR) as Role[]).map((r) => (
              <option key={r} value={r}>
                {ROLE_AR[r]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="الهاتف">
          <Input dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>

        {/*
          The technician gets a PIN and the office gets a password, and that split is enforced
          by the API (routes/users.ts). A technician will not type a password at 6am in a van.
        */}
        {isTech ? (
          <Field label="رمز PIN" hint="4 إلى 8 أرقام. الفني يدخل بالهاتف والرمز، لا بكلمة مرور.">
            <Input
              dir="ltr"
              inputMode="numeric"
              value={form.pin}
              onChange={(e) => setForm({ ...form, pin: e.target.value })}
            />
          </Field>
        ) : (
          <>
            <Field label="البريد الإلكتروني">
              <Input
                type="email"
                dir="ltr"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
            <Field label="كلمة المرور" hint="8 أحرف على الأقل.">
              <Input
                type="password"
                dir="ltr"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </Field>
          </>
        )}
      </div>

      <Button
        variant="primary"
        className="mt-4 w-full"
        disabled={create.isPending || !valid}
        onClick={() =>
          create.mutate(
            {
              name: form.name.trim(),
              phone: form.phone.trim(),
              email: form.email.trim() || undefined,
              role: form.role,
              pin: isTech ? form.pin : undefined,
              password: isTech ? undefined : form.password,
            },
            { onSuccess: onClose },
          )
        }
      >
        حفظ
      </Button>
    </Modal>
  );
}

/**
 * Clock skew per device. SYNC.md Rule 2 records it on every push for exactly this screen: a
 * phone three hours out of true is a support ticket waiting to happen, and this is where the
 * office sees it before the technician calls.
 */
function DevicesModal({ user, onClose }: { user: { id: string; name: string }; onClose: () => void }) {
  const { data, isLoading } = useDevices(user.id);

  return (
    <Modal title={`أجهزة ${user.name}`} onClose={onClose} wide>
      {isLoading ? (
        <Loading />
      ) : (data?.devices.length ?? 0) === 0 ? (
        <Empty>لم يسجّل هذا الفني أي جهاز بعد.</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>الجهاز</Th>
              <Th>المنصّة</Th>
              <Th>آخر ظهور</Th>
              <Th>انحراف الساعة</Th>
            </tr>
          </thead>
          <tbody>
            {(data?.devices ?? []).map((d) => (
              <Tr key={d.id}>
                <Td className="font-medium">{d.label ?? <span className="num">{d.id.slice(0, 8)}</span>}</Td>
                <Td className="text-ink-soft">{d.platform ?? "—"}</Td>
                <Td>
                  <span className="num text-ink-soft">{fmtDateTime(d.lastSeenAt)}</span>
                </Td>
                <Td>
                  {d.clock_skew_warning ? (
                    <Badge tone="bad">{d.clock_skew_ar}</Badge>
                  ) : (
                    <span className="num text-ink-soft">{Math.round(d.clockSkewMs / 1000)} ث</span>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      <Note className="mt-3">
        ساعة الجهاز لا تُرتّب شيئاً ولا تحسم أي تعارض — تُعرض فقط. لكن جهازاً منحرفاً بساعات يعني
        أوقاتاً غريبة أمام الفني، وهذا سبب اتصال قادم بالدعم.
      </Note>
    </Modal>
  );
}

// ── job types ────────────────────────────────────────────────────────────────

function JobTypes() {
  const { data, isLoading, error } = useJobTypes();
  const create = useCreateJobType();
  const [form, setForm] = useState({ name_ar: "", default_duration_mins: "60" });

  return (
    <>
      {error ? <ErrorNote error={error} className="mb-4" /> : null}
      {create.error ? <ErrorNote error={create.error} className="mb-4" /> : null}

      <Card title="أنواع المهام" className="mb-4" bodyClassName="">
        {isLoading ? (
          <Loading />
        ) : (data?.job_types.length ?? 0) === 0 ? (
          <Empty>لا توجد أنواع مهام. أضِف نوعاً — تركيب، صيانة، تصليح، فحص…</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>الاسم</Th>
                <Th>المدة الافتراضية</Th>
                <Th>اللون</Th>
                <Th>الحالة</Th>
              </tr>
            </thead>
            <tbody>
              {(data?.job_types ?? []).map((t) => (
                <Tr key={t.id}>
                  <Td className="font-medium">{t.nameAr}</Td>
                  <Td>
                    <span className="num">{t.defaultDurationMins}</span> دقيقة
                  </Td>
                  <Td>
                    {t.color ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="size-3 rounded-full border border-line" style={{ background: t.color }} />
                        <span className="num text-xs text-ink-faint">{t.color}</span>
                      </span>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td>
                    <Badge tone={t.active ? "good" : "neutral"}>{t.active ? "فعّال" : "معطّل"}</Badge>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card title="إضافة نوع">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="الاسم" className="w-64">
            <Input
              value={form.name_ar}
              placeholder="مثلاً: صيانة دورية"
              onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
            />
          </Field>
          <Field label="المدة الافتراضية (دقيقة)" className="w-48">
            <Input
              type="number"
              dir="ltr"
              min={1}
              value={form.default_duration_mins}
              onChange={(e) => setForm({ ...form, default_duration_mins: e.target.value })}
            />
          </Field>
          <Button
            variant="primary"
            disabled={create.isPending || !form.name_ar.trim()}
            onClick={() =>
              create.mutate(
                {
                  name_ar: form.name_ar.trim(),
                  default_duration_mins: Math.max(1, Math.round(Number(form.default_duration_mins) || 60)),
                },
                { onSuccess: () => setForm({ name_ar: "", default_duration_mins: "60" }) },
              )
            }
          >
            إضافة
          </Button>
        </div>
      </Card>
    </>
  );
}

// ── checklists ───────────────────────────────────────────────────────────────

const CHECKLIST_KIND_AR: Record<string, string> = {
  check: "علامة",
  text: "نص",
  number: "رقم",
  photo: "صورة",
};

function Checklists() {
  const { data, isLoading } = useChecklists();
  const jobTypes = useJobTypes();
  const create = useCreateChecklist();

  const [jobTypeId, setJobTypeId] = useState("");
  const [items, setItems] = useState<Array<{ label_ar: string; kind: string; required: boolean }>>([
    { label_ar: "", kind: "check", required: false },
  ]);

  const typeName = (id: string) =>
    jobTypes.data?.job_types.find((t) => t.id === id)?.nameAr ?? id;

  const valid = jobTypeId && items.some((i) => i.label_ar.trim());

  return (
    <>
      {create.error ? <ErrorNote error={create.error} className="mb-4" /> : null}

      <div className="mb-4 space-y-3">
        {isLoading ? (
          <Loading />
        ) : (data?.checklists.length ?? 0) === 0 ? (
          <Card title="قوائم الفحص">
            <Note>
              لا توجد قوائم فحص. القائمة تُملأ على جهاز الفني وتصبح جزءاً من دليل المهمة — وهي
              أسهل طريقة لفرض «الشغلة تنعمل صح» بلا مكالمة.
            </Note>
          </Card>
        ) : (
          (data?.checklists ?? []).map((c) => (
            <Card key={c.id} title={`قائمة فحص — ${typeName(c.jobTypeId)}`} bodyClassName="">
              <Table>
                <thead>
                  <tr>
                    <Th>البند</Th>
                    <Th>النوع</Th>
                    <Th>إلزامي</Th>
                  </tr>
                </thead>
                <tbody>
                  {c.items.map((i) => (
                    <Tr key={i.id}>
                      <Td className="font-medium">{i.labelAr}</Td>
                      <Td>{CHECKLIST_KIND_AR[i.kind] ?? i.kind}</Td>
                      <Td>
                        <Badge tone={i.required ? "warn" : "neutral"}>{i.required ? "نعم" : "لا"}</Badge>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          ))
        )}
      </div>

      <Card title="قائمة فحص جديدة">
        <Field label="نوع المهمة" className="mb-3 w-64">
          <Select value={jobTypeId} onChange={(e) => setJobTypeId(e.target.value)}>
            <option value="">— اختر —</option>
            {(jobTypes.data?.job_types ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.nameAr}
              </option>
            ))}
          </Select>
        </Field>

        <div className="space-y-2">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-end gap-2">
              <Field label={`البند ${idx + 1}`} className="flex-1">
                <Input
                  value={item.label_ar}
                  placeholder="مثلاً: قياس ضغط الغاز"
                  onChange={(e) => {
                    const next = [...items];
                    next[idx] = { ...item, label_ar: e.target.value };
                    setItems(next);
                  }}
                />
              </Field>
              <Field label="النوع" className="w-28">
                <Select
                  value={item.kind}
                  onChange={(e) => {
                    const next = [...items];
                    next[idx] = { ...item, kind: e.target.value };
                    setItems(next);
                  }}
                >
                  {Object.entries(CHECKLIST_KIND_AR).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </Select>
              </Field>
              <label className="flex items-center gap-1.5 pb-2 text-xs">
                <input
                  type="checkbox"
                  checked={item.required}
                  onChange={(e) => {
                    const next = [...items];
                    next[idx] = { ...item, required: e.target.checked };
                    setItems(next);
                  }}
                />
                إلزامي
              </label>
              <Button
                variant="ghost"
                className="mb-1"
                onClick={() => setItems(items.filter((_, i) => i !== idx))}
                disabled={items.length === 1}
              >
                ✕
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-3 flex gap-2">
          <Button onClick={() => setItems([...items, { label_ar: "", kind: "check", required: false }])}>
            بند آخر
          </Button>
          <Button
            variant="primary"
            disabled={create.isPending || !valid}
            onClick={() =>
              create.mutate(
                {
                  job_type_id: jobTypeId,
                  items: items.filter((i) => i.label_ar.trim()).map((i) => ({ ...i, label_ar: i.label_ar.trim() })),
                },
                {
                  onSuccess: () => {
                    setItems([{ label_ar: "", kind: "check", required: false }]);
                    setJobTypeId("");
                  },
                },
              )
            }
          >
            حفظ القائمة
          </Button>
        </div>
      </Card>
    </>
  );
}

// ── custom fields ────────────────────────────────────────────────────────────

const FIELD_KIND_AR: Record<string, string> = {
  text: "نص",
  number: "رقم",
  bool: "نعم/لا",
  select: "قائمة اختيار",
  date: "تاريخ",
};

const ENTITY_AR: Record<string, string> = {
  job: "المهمة",
  customer: "الزبون",
  site: "الموقع",
};

function CustomFields() {
  const { data, isLoading } = useCustomFields();
  const jobTypes = useJobTypes();
  const create = useCreateCustomField();
  const [form, setForm] = useState({
    entity: "job",
    job_type_id: "",
    key: "",
    label_ar: "",
    kind: "text",
    options: "",
    required: false,
  });

  const keyOk = /^[a-z0-9_]+$/.test(form.key);

  return (
    <>
      {create.error ? <ErrorNote error={create.error} className="mb-4" /> : null}

      <Card title="الحقول المخصّصة" className="mb-4" bodyClassName="">
        {isLoading ? (
          <Loading />
        ) : (data?.custom_fields.length ?? 0) === 0 ? (
          <Empty>
            لا توجد حقول مخصّصة. أي بيان تحتاجه شركتك ولا يوفّره البرنامج يُضاف هنا كحقل — لا كتعديل
            على البرنامج.
          </Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>التسمية</Th>
                <Th>المفتاح</Th>
                <Th>على</Th>
                <Th>النوع</Th>
                <Th>نوع المهمة</Th>
                <Th>إلزامي</Th>
              </tr>
            </thead>
            <tbody>
              {(data?.custom_fields ?? []).map((f) => (
                <Tr key={f.id}>
                  <Td className="font-medium">{f.labelAr}</Td>
                  <Td>
                    <span className="num text-ink-soft">{f.key}</span>
                  </Td>
                  <Td>{ENTITY_AR[f.entity]}</Td>
                  <Td>
                    {FIELD_KIND_AR[f.kind]}
                    {f.kind === "select" && f.options.length > 0 ? (
                      <span className="text-xs text-ink-faint"> ({f.options.join("، ")})</span>
                    ) : null}
                  </Td>
                  <Td className="text-ink-soft">
                    {f.jobTypeId
                      ? (jobTypes.data?.job_types.find((t) => t.id === f.jobTypeId)?.nameAr ?? "—")
                      : "كل الأنواع"}
                  </Td>
                  <Td>
                    <Badge tone={f.required ? "warn" : "neutral"}>{f.required ? "نعم" : "لا"}</Badge>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card title="إضافة حقل">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          <Field label="التسمية (بالعربية)">
            <Input value={form.label_ar} onChange={(e) => setForm({ ...form, label_ar: e.target.value })} />
          </Field>
          <Field
            label="المفتاح"
            hint={form.key && !keyOk ? "حروف إنكليزية صغيرة وأرقام وشرطة سفلية فقط" : "يُخزَّن به في قاعدة البيانات"}
          >
            <Input dir="ltr" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} />
          </Field>
          <Field label="على أي شيء">
            <Select value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })}>
              {Object.entries(ENTITY_AR).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="النوع">
            <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {Object.entries(FIELD_KIND_AR).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
          {form.kind === "select" ? (
            <Field label="الخيارات" hint="افصل بينها بفاصلة">
              <Input value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} />
            </Field>
          ) : null}
          {form.entity === "job" ? (
            <Field label="خاص بنوع مهمة (اختياري)">
              <Select value={form.job_type_id} onChange={(e) => setForm({ ...form, job_type_id: e.target.value })}>
                <option value="">كل الأنواع</option>
                {(jobTypes.data?.job_types ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nameAr}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <label className="flex items-center gap-2 pt-5 text-sm">
            <input
              type="checkbox"
              checked={form.required}
              onChange={(e) => setForm({ ...form, required: e.target.checked })}
            />
            إلزامي
          </label>
        </div>

        <Button
          variant="primary"
          className="mt-3"
          disabled={create.isPending || !form.label_ar.trim() || !keyOk}
          onClick={() =>
            create.mutate(
              {
                entity: form.entity,
                job_type_id: form.entity === "job" && form.job_type_id ? form.job_type_id : null,
                key: form.key,
                label_ar: form.label_ar.trim(),
                kind: form.kind,
                options:
                  form.kind === "select"
                    ? form.options.split(/[,،]/).map((o) => o.trim()).filter(Boolean)
                    : [],
                required: form.required,
                sort: (data?.custom_fields.length ?? 0) + 1,
              },
              {
                onSuccess: () =>
                  setForm({
                    entity: "job",
                    job_type_id: "",
                    key: "",
                    label_ar: "",
                    kind: "text",
                    options: "",
                    required: false,
                  }),
              },
            )
          }
        >
          إضافة
        </Button>
      </Card>
    </>
  );
}

// ── company ──────────────────────────────────────────────────────────────────

/**
 * Stubbed, and not by choice.
 *
 * `companies` carries `tax_rate_bps`, `invoice_prefix`, `invoice_next_seq`, `plan` and a
 * `settings` jsonb that `buildInvoiceFromJob` reads `labour_rate_fils_per_hour` and
 * `callout_fee_fils` out of on every invoice it builds. Not one of them is reachable: there is
 * no GET /company and no PATCH — the API has no company route at all.
 *
 * So the labour rate that decides every invoice total is set by editing the database by hand.
 * That is the single biggest hole in this console, and it is named here rather than faked with
 * a form that posts nowhere.
 */
function Company() {
  return (
    <Card title="بيانات الشركة">
      <Note>
        غير متاحة: لا يوجد منفذ <span className="num">/company</span> في الـ API — لا للقراءة ولا
        للتعديل.
      </Note>
      <p className="mt-3 text-sm text-ink-soft">هذه القيم موجودة في قاعدة البيانات ولا يمكن ضبطها من هنا:</p>
      <ul className="mt-2 space-y-1.5 text-sm text-ink-soft">
        <Li name="tax_rate_bps">نسبة الضريبة (500 = ٥٪). معظم الشركات العراقية تعمل بصفر.</Li>
        <Li name="invoice_prefix">بادئة رقم الفاتورة (مثلاً INV).</Li>
        <Li name="invoice_next_seq">التسلسل القادم لرقم الفاتورة. يمنحه الخادم وحده.</Li>
        <Li name="settings.labour_rate_fils_per_hour">
          أجرة الساعة. تدخل في كل فاتورة كبند عمل — بدونها لا تُحتسب أجرة أصلاً.
        </Li>
        <Li name="settings.callout_fee_fils">أجرة الاستدعاء.</Li>
        <Li name="plan">الباقة: توزيع / فوترة / محاسبة.</Li>
      </ul>
      <p className="mt-3 text-xs text-ink-faint">
        أجرة الساعة تُحدِّد مجموع كل فاتورة، وضبطها اليوم يحتاج تعديلاً مباشراً على قاعدة البيانات.
        هذا أهم منفذ ناقص في الـ API.
      </p>
    </Card>
  );
}

function Li({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <code className="num shrink-0 rounded bg-line-soft px-1.5 py-0.5 text-xs text-ink">{name}</code>
      <span className="text-xs">{children}</span>
    </li>
  );
}

// ── states ───────────────────────────────────────────────────────────────────

/** The one place the product says no, stated in the UI rather than discovered in a meeting. */
function States() {
  return (
    <Card title="حالات المهمة">
      <Note className="mb-4">
        حالات المهمة ثابتة ولا تُضاف إليها حالة جديدة. هذا القرار الوحيد الذي يقول فيه فني «لا»،
        ويقولها مكتوبة: صحّة المزامنة تعتمد على أن الجهاز والخادم يعرفان نفس الرسم البياني تماماً.
        الشركة التي تحتاج حالة إضافية تحصل على حالة فرعية للعرض فقط، لا على عقدة في هذا الرسم.
      </Note>

      <Table>
        <thead>
          <tr>
            <Th>الحالة</Th>
            <Th>ينقلها المكتب إلى</Th>
          </tr>
        </thead>
        <tbody>
          {JOB_STATES.map((s) => {
            const office = allowedTransitions(s, "office");
            return (
              <Tr key={s}>
                <Td>
                  <Badge tone="neutral">{JOB_STATE_AR[s]}</Badge>
                </Td>
                <Td>
                  {office.length === 0 ? (
                    <span className="text-xs text-ink-faint">لا شيء — حالة نهائية بالنسبة للمكتب</span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {office.map((to) => (
                        <Badge key={to} tone="info">
                          {JOB_STATE_AR[to]}
                        </Badge>
                      ))}
                    </span>
                  )}
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>

      <p className="mt-3 text-xs text-ink-faint">
        الباقي ينقله الفني من جهازه. المكتب لا يستطيع أن يقول «في الطريق» نيابة عنه، والفني لا
        يستطيع أن يلغي مهمة — عمداً.
      </p>
    </Card>
  );
}
